import { useId, useState, type ReactNode } from 'react';
import { Slider } from '@mantine/core';
import clsx from 'clsx';
import AnchoredDialog from '../AnchoredDialog';
import Button from '../../atoms/Button';
import Switch from '../../atoms/Switch';
import './VoiceVolumeMenu.scss';

/** Громкость собеседника у себя: `volume` — %, `muted` — «Заглушить у меня» (громкость при этом помнится). */
export interface IVoiceVolumeValue {
    volume: number;
    muted: boolean;
}

export interface IVoiceVolumeMenuProps {
    /** Чья громкость: «Иванов И. И.» — заголовок меню и имя ползунка */
    name: string;
    volume: IVoiceVolumeValue;
    onVolumeChange: (change: Partial<IVoiceVolumeValue>) => void;
    /** Аватар или кружок собеседника — содержимое кнопки, которая открывает меню */
    children: ReactNode;
    /** Значок поверх кнопки (заглушён) — рядом с `children` внутри неё */
    badge?: ReactNode;
    /** Имя кнопки @default 'Громкость: <name>' */
    label?: string;
    /** Подсказка по наведению, если её не даёт сам аватар */
    title?: string;
    placement?: 'bottom' | 'top';
    /** Открытость снаружи (карточка собеседника по наведению не открывается, пока меню открыто) */
    opened?: boolean;
    onOpenChange?: (opened: boolean) => void;
    /** Класс кнопки */
    targetClassName?: string;
    /** Класс панели (исключение из удалённого управления и т. п.) */
    className?: string;
    /** Вторая строка «Заглушить у меня» @default 'Собеседнику об этом не сообщается' */
    muteDescription?: string;
    /** Верх ползунка, % — `VOICE_VOLUME_MAX` пакета показа @default 200 */
    max?: number;
    /** Обычная громкость, % — `VOICE_VOLUME_DEFAULT` пакета показа, к ней «Вернуть» @default 100 */
    normal?: number;
    /** Тест-якорь кнопки; панель — `<id>-menu`, «Заглушить» — `<id>-mute` @default 'voice-volume' */
    'data-testid'?: string;
}

/**
 * Громкость собеседника у себя (IMPLEMENTATION.md §42), как в Discord: кнопка-аватар — ползунок 0–200 % и «Заглушить
 * у меня»; «Вернуть 100 %», когда громкость изменена. Хранит выбор приложение (по пользователю собеседника).
 */
export default function VoiceVolumeMenu({
    name,
    volume,
    onVolumeChange,
    children,
    badge,
    label,
    title,
    placement = 'bottom',
    opened: outerOpened,
    onOpenChange,
    targetClassName,
    className,
    muteDescription = 'Собеседнику об этом не сообщается',
    max = 200,
    normal = 100,
    'data-testid': testId = 'voice-volume',
}: IVoiceVolumeMenuProps) {
    const [ownOpened, setOwnOpened] = useState(false);
    const opened = outerOpened ?? ownOpened;
    const setOpened = (next: boolean) => {
        setOwnOpened(next);
        onOpenChange?.(next);
    };
    const titleId = useId();
    return (
        <AnchoredDialog
            opened={opened}
            onClose={() => setOpened(false)}
            placement={placement}
            align="center"
            width={260}
            className={className}
            dropdownAria={{ 'aria-labelledby': titleId }}
            data-testid={`${testId}-menu`}
            target={
                <button
                    type="button"
                    className={clsx('rf-voice-volume__target', targetClassName)}
                    aria-label={label ?? `Громкость: ${name}`}
                    title={title}
                    aria-haspopup="dialog"
                    aria-expanded={opened}
                    data-muted={volume.muted || undefined}
                    data-testid={testId}
                    onClick={() => setOpened(!opened)}
                >
                    {children}
                    {badge}
                </button>
            }
        >
            <div className="rf-voice-volume">
                <div className="rf-voice-volume__head">
                    <strong id={titleId}>{name}</strong>
                    <span className="rf-voice-volume__value" aria-hidden>
                        {volume.muted ? 'заглушён' : `${volume.volume} %`}
                    </span>
                </div>
                <Slider
                    className="rf-voice-volume__slider"
                    min={0}
                    max={max}
                    step={5}
                    marks={[{ value: 0 }, { value: normal }, { value: max }]}
                    value={volume.volume}
                    disabled={volume.muted}
                    label={null}
                    thumbLabel={`Громкость: ${name}`}
                    onChange={(value) => onVolumeChange({ volume: value })}
                />
                <Switch
                    size="sm"
                    between
                    reverse
                    label="Заглушить у меня"
                    description={muteDescription}
                    checked={volume.muted}
                    onCheckedChange={(muted) => onVolumeChange({ muted })}
                    data-testid={`${testId}-mute`}
                />
                {volume.volume !== normal && (
                    <Button
                        variant="text"
                        size="compact"
                        className="rf-voice-volume__reset"
                        onClick={() => onVolumeChange({ volume: normal })}
                    >
                        Вернуть {normal} %
                    </Button>
                )}
            </div>
        </AnchoredDialog>
    );
}
